import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { Check, ShoppingBag } from 'lucide-react'
import { formatPrice } from '@/lib/format'
import { useCart } from '@/components/generals/cart-provider'
import { primaryButton } from '@/components/generals/button-styles'
import QuantityStepper from '@/components/shop/quantity-stepper'

interface AddToCartProps {
    productId: string
    price: number
    available: boolean
}

const AddToCart: FC<AddToCartProps> = ({ productId, price, available }): ReactNode => {
    const { addItem } = useCart()
    const [quantity, setQuantity] = useState(1)
    const [added, setAdded] = useState(false)
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current)
    }, [])

    useEffect(() => {
        setQuantity(1)
        setAdded(false)
    }, [productId])

    const add = (): void => {
        addItem(productId, quantity)
        setAdded(true)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => setAdded(false), 6000)
    }

    if (!available) {
        return (
            <div className='rounded-[3px] border border-dashed border-bark/30 px-5 py-4'>
                <p className='font-heading text-lg text-bark'>Sold out for now</p>
                <p className='mt-1 text-sm text-bark-soft'>
                    A new batch is drying.{' '}
                    <Link href='/contact' className='ink-link pb-0.5 text-pomegranate'>Ask us when it is ready</Link>
                </p>
            </div>
        )
    }

    return (
        <div>
            <div className='flex flex-wrap items-stretch gap-3'>
                <QuantityStepper value={quantity} onChange={setQuantity} label='Quantity' />
                <button type='button' onClick={add} className={`${primaryButton} flex-1 whitespace-nowrap`}>
                    {added ? <Check className='size-4' strokeWidth={2} /> : <ShoppingBag className='size-4' strokeWidth={1.75} />}
                    {added ? 'Added to cart' : `Add to cart, ${formatPrice(price * quantity)}`}
                </button>
            </div>

            <p aria-live='polite' className='mt-3 min-h-6 text-sm text-bark-soft'>
                {added && (
                    <>
                        In your cart.{' '}
                        <Link href='/cart' className='ink-link pb-0.5 font-medium text-pomegranate'>View cart and send your order</Link>
                    </>
                )}
            </p>
        </div>
    )
}

export default AddToCart
